import React from 'react';
import { PublicSplitData } from '../types';

interface AlreadySignedComponentProps {
  signData: PublicSplitData & {
    signedAt?: string;
    signedName?: string;
  };
}

const AlreadySignedComponent: React.FC<AlreadySignedComponentProps> = ({
  signData
}) => {
  const formatDate = (dateString?: string) => {
    if (!dateString) return 'recently';
    try {
      return new Date(dateString).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch {
      return 'recently';
    }
  };

  return (
    <div className="min-h-screen bg-[#18181B] flex items-center justify-center p-4">
      <div className="max-w-2xl w-full bg-[#27272A] rounded-2xl shadow-xl p-8 text-center border border-[#3F3F46]">
        {/* Status Icon */}
        <div className="mb-6">
          <div className="mx-auto w-16 h-16 bg-[#10B98133] rounded-full flex items-center justify-center border border-[#10B98130]">
            <svg className="w-8 h-8 text-[#10B981]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} 
                d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
        </div>

        {/* Main Heading */}
        <h1 className="text-3xl font-bold text-white mb-4">
          Already Confirmed
        </h1>

        {/* Success Information */}
        <div className="bg-[#10B98115] border-l-4 border-[#10B981] rounded-2xl p-4 mb-6 text-left border border-[#10B98130]">
          <div className="flex">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-[#10B981]" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="ml-3">
              <p className="text-[#34D399]">
                You have already confirmed your share of <strong>"{signData.splitTitle}"</strong>.
              </p>
              {signData.signedAt && (
                <p className="text-[#10B981] mt-1">
                  Confirmed on {formatDate(signData.signedAt)}
                  {signData.signedName && (
                    <> as <strong>{signData.signedName}</strong></>
                  )}
                </p>
              )}
            </div>
          </div>
        </div>

        {/* Split Details */}
        <div className="bg-[#3F3F46] rounded-2xl p-6 mb-6 border border-[#3F3F46]">
          <h3 className="text-lg font-semibold text-[#9CA3AF] mb-4">Split Details</h3>
          
          <div className="grid gap-4 text-left">
            <div className="flex justify-between items-center py-2 border-b border-[#52525B]">
              <span className="text-[#9CA3AF]">Song Title:</span>
              <span className="font-medium text-white">{signData.splitTitle}</span>
            </div>
            
            <div className="flex justify-between items-center py-2 border-b border-[#52525B]">
              <span className="text-[#9CA3AF]">Your Role:</span>
              <span className="font-medium text-white capitalize">
                {signData.collaborator.role === 'both' ? 'Composer & Author' : signData.collaborator.role}
              </span>
            </div>
            
            <div className="flex justify-between items-center py-2 border-b border-[#52525B]">
              <span className="text-[#9CA3AF]">Your Share:</span>
              <span className="font-medium text-white">{signData.collaborator.share}%</span>
            </div>
            
            <div className="flex justify-between items-center py-2">
              <span className="text-[#9CA3AF]">Split Creator:</span>
              <span className="font-medium text-white">{signData.ownerName}</span>
            </div>
          </div>
        </div>

        {/* Status Message */}
        <div className="text-center mb-8">
          <p className="text-[#9CA3AF]">
            Once all collaborators have confirmed, you'll receive a confirmation email with the final record.
          </p>
        </div>

        {/* Help Section */}
        <div className="border-t border-[#3F3F46] pt-6 mb-6">
          <h4 className="font-medium text-white mb-2">Questions about this split?</h4>
          <p className="text-[#9CA3AF] text-sm">
            Contact the split creator ({signData.ownerName}) or reach out to support at{' '}
            <a 
              href="mailto:help@cotune.app" 
              className="text-[#14B8A6] hover:text-[#0D9488] underline"
            >
              help@cotune.app
            </a>
          </p>
        </div>

        {/* Action Button */}
        <button 
          onClick={() => window.close()}
          className="btn-primary"
        >
          Close
        </button>
      </div>
    </div>
  );
};

export default AlreadySignedComponent;
